<script setup lang="ts">
import { computed, ref, watch } from "vue";
import Modal from "./Modal.vue";
import Button from "./Button.vue";
import Select from "./Select.vue";
import type { Member, Team } from "../api/types";
import { t } from "../i18n";
const props = defineProps<{
  open: boolean;
  mode: "assign" | "transfer";
  members: Member[];
  teams: Team[];
  busy?: boolean;
}>();
const emit = defineEmits<{
  close: [];
  confirm: [
    payload: { memberId: string | null; teamId: string | null; note?: string },
  ];
}>();
const team = ref("");
const member = ref("");
const note = ref("");
const teamOptions = computed(() => [
  t("transfer.keep_team"),
  ...props.teams.filter((x) => x.is_active).map((x) => x.name),
]);
const selectedTeam = computed(
  () => props.teams.find((x) => x.name === team.value) ?? null,
);
const memberPool = computed(() =>
  (selectedTeam.value
    ? props.members.filter((m) =>
        selectedTeam.value!.members.some((tm) => tm.id === m.id),
      )
    : props.members
  ).filter((m) => m.status === "active"),
);
const memberOptions = computed(() => [
  props.mode === "transfer" ? t("transfer.any_member") : t("assign.unassigned"),
  ...memberPool.value.map((m) => m.name),
]);
const selectedMember = computed(
  () => memberPool.value.find((m) => m.name === member.value) ?? null,
);
watch(
  () => props.open,
  (v) => {
    if (v) {
      team.value = teamOptions.value[0] ?? "";
      member.value = memberOptions.value[0] ?? "";
      note.value = "";
    }
  },
);
function confirm() {
  emit("confirm", {
    memberId: selectedMember.value?.id ?? null,
    teamId: selectedTeam.value?.id ?? null,
    ...(note.value.trim() ? { note: note.value.trim() } : {}),
  });
}
</script>
<template>
  <Modal
    :open="open"
    :title="mode === 'transfer' ? t('transfer.title') : t('assign.title')"
    @close="emit('close')"
  >
    <div class="modal-form">
      <Select
        v-if="mode === 'transfer'"
        v-model="team"
        :label="t('transfer.team')"
        :options="teamOptions"
      />
      <Select
        v-model="member"
        :label="mode === 'transfer' ? t('transfer.member') : t('assign.member')"
        :options="memberOptions"
      />
      <label v-if="mode === 'transfer'" class="wa-field"
        ><span>{{ t("transfer.note") }}</span
        ><textarea placeholder=" " v-model="note" rows="2" />
      </label>
    </div>
    <template #footer>
      <Button variant="secondary" @click="emit('close')">{{
        t("common.cancel")
      }}</Button>
      <Button
        :loading="busy"
        :disabled="mode === 'transfer' && !selectedTeam && !selectedMember"
        @click="confirm"
        >{{
          mode === "transfer" ? t("transfer.confirm") : t("assign.confirm")
        }}</Button
      >
    </template>
  </Modal>
</template>
